import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from "@/components/theme-provider";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel, DropdownMenuGroup, DropdownMenuShortcut } from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger } from "@/components/ui/navigation-menu";
import { Separator } from "@/components/ui/separator";
import { User, Menu, X, Check, ChevronsUpDown, ChevronDown, Settings, Plus, HelpCircle, LogOut, ChevronRight } from "lucide-react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import Logo from "@/components/logo";
import { useAuth } from "../context/AuthContext";
import { HiOutlineSparkles } from "react-icons/hi";
import AICommandPalette, { contextHelpers } from "@/components/AiCommandPalette";
import { Toaster } from "@/components/ui/toaster";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import NotificationDropdown from "@/components/NotificationDropdown";
import { Label } from "@/components/ui/label";
import { ModeToggle } from "@/components/mode-toggle";
import { useAiPageContext } from "@/context/AiPageContextProvider";
import { useCreateTask } from "@/context/CreateTaskContext";
import { useGlobalLoading } from "@/context/GlobalLoadingContext";
import GlobalLoadingIndicator from "@/components/GlobalLoadingIndicator";
// import statusBotCircleIcon from "../assets/status-bot-circle-icon.svg";
// import statusBotIcon from "../assets/statusBotIcon-vivid.svg";
import statusBotSticker from "../assets/statusbot-sticker.svg";

const Layout = ({ children, noContainer = false }) => {
    const [isNavVisible, setIsNavVisible] = useState(true);
    const [lastScrollY, setLastScrollY] = useState(0);
    const { theme, setTheme } = useTheme();
    const { logout, user, activeOrganization, setActiveOrganization, axiosInstance } = useAuth();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
    const [organizations, setOrganizations] = useState([]);
    const location = useLocation();
    const navigate = useNavigate();
    const { organizationSlug } = useParams();
    const currentOrgSlug = organizationSlug || activeOrganization?.slug;
    const [isLoadingOrg, setIsLoadingOrg] = useState(true);
    const [activeSection, setActiveSection] = useState('');
    const [isOrgExpanded, setIsOrgExpanded] = useState(false);
    const { currentAiPageContext, setCurrentAiPageContext } = useAiPageContext();
    const [initialAskQuery, setInitialAskQuery] = useState(null);
    
    // Recent projects state
    const [recentProjects, setRecentProjects] = useState([]);
    const [totalProjectsCount, setTotalProjectsCount] = useState(0);
    const [isLoadingRecentProjects, setIsLoadingRecentProjects] = useState(true);
    const [projectsError, setProjectsError] = useState(null);
    
    // Priority monitors state
    const [priorityMonitors, setPriorityMonitors] = useState([]);
    const [totalMonitorsCount, setTotalMonitorsCount] = useState(0);
    const [isLoadingPriorityMonitors, setIsLoadingPriorityMonitors] = useState(true);
    const [monitorsError, setMonitorsError] = useState(null);
    
    // Create task dialog from context
    const { openCreateTaskDialog } = useCreateTask();
    
    // Global loading indicator
    const { loadingRef } = useGlobalLoading();
    
    // Time refresh state - updates every 30 seconds to keep "time ago" displays accurate
    const [, setTimeRefresh] = useState(0);

    const fetchRecentProjects = useCallback(async () => {
        setIsLoadingRecentProjects(true);
        setProjectsError(null);
        try {
            const response = await axiosInstance.get('/projects/recent');
            setRecentProjects(response.data.projects || []);
            setTotalProjectsCount(response.data.totalCount || 0);
        } catch (error) {
            console.error('Error fetching recent projects:', error);
            setProjectsError(error.message || 'Failed to load projects');
            setRecentProjects([]);
            setTotalProjectsCount(0);
        } finally {
            setIsLoadingRecentProjects(false);
        }
    }, [axiosInstance]);

    const fetchPriorityMonitors = useCallback(async () => {
        setIsLoadingPriorityMonitors(true);
        setMonitorsError(null);
        try {
            const response = await axiosInstance.get('/monitors/priority');
            setPriorityMonitors(response.data.monitors || []);
            setTotalMonitorsCount(response.data.totalCount || 0);
        } catch (error) {
            console.error('Error fetching priority monitors:', error);
            setMonitorsError(error.message || 'Failed to load monitors');
            setPriorityMonitors([]);
            setTotalMonitorsCount(0);
        } finally {
            setIsLoadingPriorityMonitors(false);
        }
    }, [axiosInstance]);

    useEffect(() => {
        const fetchOrganizations = async () => {
            setIsLoadingOrg(true);
            try {
                const response = await axiosInstance.get('/organizations');
                setOrganizations(response.data);

                if (currentOrgSlug && response.data.length > 0) {
                    const orgExists = response.data.some(org => org.slug === currentOrgSlug);
                    if (!orgExists) {
                        setActiveOrganization(response.data[0]);
                    }
                } else if (!currentOrgSlug && response.data.length > 0) {
                    setActiveOrganization(response.data[0]);
                }
            } catch (error) {
                console.error('Error fetching organizations:', error);
            } finally {
                setIsLoadingOrg(false);
            }
        };
        fetchOrganizations();
        fetchRecentProjects();
        fetchPriorityMonitors();
    }, [fetchRecentProjects, fetchPriorityMonitors]);

    useEffect(() => {
        if (organizationSlug && organizations.length > 0) {
            try {
                if (organizationSlug !== activeOrganization?.slug || activeOrganization?.name !== organizations.find(org => org.slug === organizationSlug)?.name) {
                    setActiveOrganization(organizations.find(org => org.slug === organizationSlug));
                }
            } catch (error) {
                console.error('Error setting active organization:', error);
            }
        }
    }, [organizationSlug, organizations]);

    useEffect(() => {
        const controlNavbar = () => {
            if (typeof window !== 'undefined') {
                if (window.scrollY <= 20) {
                    setIsNavVisible(true);
                } else if (window.scrollY > lastScrollY) {
                    setIsNavVisible(false);
                } else {
                    setIsNavVisible(true);
                }

                setLastScrollY(window.scrollY);
            }
        };

        const handleResize = () => {
            if (window.innerWidth >= 768) { // md breakpoint
                setIsMobileMenuOpen(false);
                document.body.style.overflow = 'unset';
            }
        };

        if (typeof window !== 'undefined') {
            window.addEventListener('scroll', controlNavbar);
            window.addEventListener('resize', handleResize);

            return () => {
                window.removeEventListener('scroll', controlNavbar);
                window.removeEventListener('resize', handleResize);
            };
        }
    }, [lastScrollY]);

    // Update time displays every 30 seconds for accuracy
    useEffect(() => {
        const interval = setInterval(() => {
            setTimeRefresh(prev => prev + 1);
        }, 30000); // Update every 30 seconds

        return () => clearInterval(interval);
    }, []);

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
        document.body.style.overflow = !isMobileMenuOpen ? 'hidden' : 'unset';
    };

    const getInitials = (name) => {
        if (!name || !name.trim()) return 'U';
        
        const trimmedName = name.trim();
        const nameParts = trimmedName.split(/\s+/).filter(part => part.length > 0);
        
        if (nameParts.length === 0) return 'U';
        
        if (nameParts.length === 1) {
            // Single name: take first two characters
            const singleName = nameParts[0];
            return singleName.length === 1 
                ? singleName.toUpperCase() 
                : singleName.substring(0, 2).toUpperCase();
        }
        
        // Multiple names: first char of first name + first char of last name
        const first = nameParts[0][0] || '';
        const last = nameParts[nameParts.length - 1][0] || '';
        return (first + last).toUpperCase();
    };

    const formatTimeSince = (date) => {
        const now = new Date();
        const diffMs = now - date;
        const diffSecs = Math.floor(diffMs / 1000);
        const diffMins = Math.floor(diffSecs / 60);
        const diffHours = Math.floor(diffMins / 60);
        const diffDays = Math.floor(diffHours / 24);
        
        if (diffSecs < 60) return `< 1m ago`;
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        return `${diffDays}d ago`;
    };

    const isLinkActive = (path) => {
        // Handle root path and today page
        if (path === '/') {
            return location.pathname === '/today';
        }

        // First check if the path matches exactly (for non-org-scoped routes)
        if (location.pathname.startsWith(path)) {
            return true;
        }

        // Then check for org-scoped routes
        const pathParts = location.pathname.split('/');
        if (pathParts.length >= 2) {
            // Remove the organization slug and rejoin
            const pathWithoutOrg = ['', ...pathParts.slice(2)].join('/');

            // Check if this path starts with the given path
            return pathWithoutOrg.startsWith(path);
        }

        return false;
    };

    const handleKeyDown = useCallback((event) => {
        // Check for CMD+K (Mac) or Ctrl+K (Windows/Linux)
        if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
            event.preventDefault();
            setIsCommandPaletteOpen(prev => !prev);
        }
    }, []);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown]);

    // Listen for chat-with-statusbot events
    useEffect(() => {
        function handleChatWithStatusBot(event) {
            const query = event.detail?.query || null;
            if (query) setInitialAskQuery(query);
            setIsCommandPaletteOpen(true);
        }
        window.addEventListener('chat-with-statusbot', handleChatWithStatusBot);
        return () => window.removeEventListener('chat-with-statusbot', handleChatWithStatusBot);
    }, []);

    const handleAskConsumed = useCallback(() => {
        // Clear the one-shot query once consumed
        setInitialAskQuery(null);
    }, []);

    return (
        <div className="min-h-[calc(100vh-4rem)] min-w-full bg-background">
            <nav className={`border-b fixed top-0 left-0 right-0 z-50 bg-background transition-all duration-300 ${isNavVisible ? 'translate-y-0' : '-translate-y-full'
                } ${isMobileMenuOpen ? 'h-screen md:h-auto' : ''}`}>
                <div className="mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex items-center">
                            <Logo className="mr-8" />
                            <div className="hidden md:flex space-x-4 items-center">
                                <Button
                                    variant={isLinkActive('/') ? "secondary" : "ghost"}
                                    onClick={() => navigate('/today')}
                                >
                                    Today
                                </Button>
                                <NavigationMenu className="hidden md:block">
                                    <NavigationMenuList>
                                        <NavigationMenuItem>
                                            <NavigationMenuTrigger 
                                                className={`data-[state=open]:bg-accent ${isLinkActive('/plan') ? "bg-secondary" : ""}`}
                                                onClick={() => navigate('/plan')}
                                            >
                                                Planner
                                            </NavigationMenuTrigger>
                                            <NavigationMenuContent>
                                                <ul className="grid gap-0.5 p-1.5 min-w-[180px]">
                                                    <li>
                                                        <div className="flex items-center gap-0.5">
                                                            <NavigationMenuLink asChild className="flex-1">
                                                                <button 
                                                                    onClick={() => navigate('/plan/tasks')}
                                                                    className="flex-1 select-none rounded-l-md px-3 py-1.5 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground text-left text-sm font-medium"
                                                                >
                                                                    All Tasks
                                                                </button>
                                                            </NavigationMenuLink>
                                                            <button 
                                                                onClick={() => {
                                                                    openCreateTaskDialog({
                                                                        onSuccess: () => {
                                                                            // Refetch recent projects when a task is created
                                                                            fetchRecentProjects();
                                                                        }
                                                                    });
                                                                }}
                                                                className="select-none rounded-r-md px-2.5 py-1.5 leading-none outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                                                                title="Create new task"
                                                            >
                                                                <Plus className="h-3.5 w-3.5" />
                                                            </button>
                                                        </div>
                                                    </li>
                                                    
                                                    {(isLoadingRecentProjects || recentProjects.length > 0 || projectsError) && (
                                                        <>
                                                            <li className="my-0.5">
                                                                <Separator />
                                                            </li>
                                                            
                                                            {isLoadingRecentProjects ? (
                                                                <>
                                                                    {[1, 2, 3].map((i) => (
                                                                        <li key={i}>
                                                                            <div className="block select-none rounded-md px-3 py-1.5">
                                                                                <Skeleton className="h-3 w-full mb-1" />
                                                                                <Skeleton className="h-2.5 w-12" />
                                                                            </div>
                                                                        </li>
                                                                    ))}
                                                                </>
                                                            ) : projectsError ? (
                                                                <li>
                                                                    <div className="px-3 py-2 text-xs text-red-500 dark:text-red-400">
                                                                        Failed to load projects
                                                                    </div>
                                                                </li>
                                                            ) : recentProjects.length > 0 ? (
                                                                <>
                                                                    {recentProjects.map((project) => (
                                                                        <li key={project.id || project._id || Math.random()}>
                                                                            <NavigationMenuLink asChild>
                                                                                <button 
                                                                                    onClick={() => navigate(`/${project.organizationSlug}/plan/${project.key}`)}
                                                                                    className="block select-none rounded-md px-3 py-1.5 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground w-full text-left"
                                                                                >
                                                                                    <div className="text-sm font-medium leading-tight">{project.name || 'Unnamed Project'}</div>
                                                                                    <div className="text-[11px] text-muted-foreground mt-0.5">{project.key || 'N/A'}</div>
                                                                                </button>
                                                                            </NavigationMenuLink>
                                                                        </li>
                                                                    ))}
                                                                </>
                                                            ) : (
                                                                <li>
                                                                    <div className="px-3 py-2 text-xs text-muted-foreground">
                                                                        No recent projects
                                                                    </div>
                                                                </li>
                                                            )}
                                                        </>
                                                    )}
                                                    
                                                    <li className="my-0.5">
                                                        <Separator />
                                                    </li>
                                                    <li>
                                                        <button 
                                                            onClick={() => navigate('/plan')}
                                                            className="w-full flex items-center justify-between px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors rounded-md hover:bg-accent"
                                                        >
                                                            <span>{totalProjectsCount} project{totalProjectsCount !== 1 ? 's' : ''}</span>
                                                            <span>View all →</span>
                                                        </button>
                                                    </li>
                                                </ul>
                                            </NavigationMenuContent>
                                        </NavigationMenuItem>
                                    </NavigationMenuList>
                                </NavigationMenu>
                                <NavigationMenu className="hidden md:block">
                                    <NavigationMenuList>
                                        <NavigationMenuItem>
                                            <NavigationMenuTrigger 
                                                className={`data-[state=open]:bg-accent ${isLinkActive('/monitors') ? "bg-secondary" : ""}`}
                                                onClick={() => navigate('/monitors')}
                                            >
                                                Monitor
                                            </NavigationMenuTrigger>
                                            <NavigationMenuContent>
                                                <ul className="grid gap-0.5 p-1.5 min-w-[280px]">
                                                    {(isLoadingPriorityMonitors || priorityMonitors.length > 0 || monitorsError) && (
                                                        <>
                                                            {isLoadingPriorityMonitors ? (
                                                                <>
                                                                    {[1, 2, 3].map((i) => (
                                                                        <li key={i}>
                                                                            <div className="block select-none rounded-md px-3 py-1.5">
                                                                                <Skeleton className="h-3 w-full mb-1" />
                                                                                <Skeleton className="h-2.5 w-32" />
                                                                            </div>
                                                                        </li>
                                                                    ))}
                                                                </>
                                                            ) : monitorsError ? (
                                                                <li>
                                                                    <div className="px-3 py-2 text-xs text-red-500 dark:text-red-400">
                                                                        Failed to load monitors
                                                                    </div>
                                                                </li>
                                                            ) : priorityMonitors.length > 0 ? (
                                                                <>
                                                                    {priorityMonitors.map((monitor) => {
                                                                        const timeSinceRun = monitor.lastRunTime 
                                                                            ? formatTimeSince(new Date(monitor.lastRunTime))
                                                                            : null;
                                                                        const responseTime = monitor.lastRunResponseTime || null;
                                                                        
                                                                        return (
                                                                            <li key={monitor.id || monitor._id || Math.random()}>
                                                                                <NavigationMenuLink asChild>
                                                                                    <button 
                                                                                        onClick={() => navigate(`/monitors/${monitor.id}`)}
                                                                                        className={`block select-none rounded-md px-3 py-1.5 leading-none no-underline outline-none transition-colors w-full text-left ${
                                                                                            !monitor.lastRunSuccess 
                                                                                                ? 'border border-red-300/50 dark:border-red-700/50 hover:bg-red-50 dark:hover:bg-red-950/30 focus:bg-red-50 dark:focus:bg-red-950/30' 
                                                                                                : 'hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground'
                                                                                        }`}
                                                                                    >
                                                                                        {/* Monitor name and status indicator */}
                                                                                        <div className="flex items-center justify-between gap-2 mb-0.5">
                                                                                            <div className="text-sm font-medium leading-tight truncate flex-1">
                                                                                                {monitor.name || 'Unnamed Monitor'}
                                                                                            </div>
                                                                                            {monitor.lastRunSuccess ? (
                                                                                                <Check className="h-3.5 w-3.5 flex-shrink-0 text-emerald-600 dark:text-emerald-400" title="Healthy" />
                                                                                            ) : (
                                                                                                <X className="h-3.5 w-3.5 flex-shrink-0 text-red-600 dark:text-red-400" title="Failing" />
                                                                                            )}
                                                                                        </div>
                                                                                        
                                                                                        {/* Condensed metadata row */}
                                                                                        <div className="flex items-center justify-between text-[11px] gap-2">
                                                                                            <div className="flex items-center gap-1 min-w-0 text-muted-foreground">
                                                                                                {timeSinceRun && (
                                                                                                    <>
                                                                                                        <span className="truncate">{timeSinceRun}</span>
                                                                                                        <span className="opacity-40">•</span>
                                                                                                    </>
                                                                                                )}
                                                                                                <span className="truncate">{monitor.organizationName || 'Unknown'}</span>
                                                                                            </div>
                                                                                            {responseTime && (
                                                                                                <span className="text-muted-foreground font-mono flex-shrink-0">
                                                                                                    {responseTime}
                                                                                                </span>
                                                                                            )}
                                                                                        </div>
                                                                                    </button>
                                                                                </NavigationMenuLink>
                                                                            </li>
                                                                        );
                                                                    })}
                                                                </>
                                                            ) : (
                                                                <li>
                                                                    <div className="px-3 py-2 text-xs text-muted-foreground">
                                                                        No monitors yet
                                                                    </div>
                                                                </li>
                                                            )}
                                                            
                                                            <li className="my-0.5">
                                                                <Separator />
                                                            </li>
                                                        </>
                                                    )}
                                                    
                                                    <li>
                                                        <button 
                                                            onClick={() => navigate('/monitors')}
                                                            className="w-full flex items-center justify-between px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors rounded-md hover:bg-accent"
                                                        >
                                                            <span>{totalMonitorsCount} monitor{totalMonitorsCount !== 1 ? 's' : ''}</span>
                                                            <span>View all →</span>
                                                        </button>
                                                    </li>
                                                </ul>
                                            </NavigationMenuContent>
                                        </NavigationMenuItem>
                                    </NavigationMenuList>
                                </NavigationMenu>
                                <Button
                                    variant={isLinkActive('/forms') ? "secondary" : "ghost"}
                                    onClick={() => navigate('/forms')}
                                >
                                    Form
                                </Button>
                                {/* <Button
                                    variant={isLinkActive('/reports') ? "secondary" : "ghost"}
                                    onClick={() => navigate('/reports')}
                                >
                                    Reports
                                </Button> */}
                            </div>
                        </div>
                        <div className="flex items-center space-x-1">
                            {/* Full Site Loading Indicator */}
                            <GlobalLoadingIndicator ref={loadingRef} />

                            {/* Notifications */}
                            <NotificationDropdown />

                            {/* User Menu */}
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon" className="hidden md:flex rounded-full p-0">
                                        {user ? (
                                            <Avatar className="h-8 w-8">
                                                <AvatarFallback className={`${user.avatarColor || 'bg-neutral-600'} text-neutral-200 text-xs font-medium`}>
                                                    {getInitials(user.name)}
                                                </AvatarFallback>
                                            </Avatar>
                                        ) : (
                                            <Skeleton className="h-8 w-8 rounded-full" />
                                        )}
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-60">
                                    {/* User Info Header - Clickable */}
                                    {user && (
                                        <>
                                            <DropdownMenuItem 
                                                onClick={() => navigate('/preferences')}
                                                className="flex items-center gap-3 px-2 py-2 cursor-pointer"
                                            >
                                                <Avatar className="h-9 w-9">
                                                    <AvatarFallback className={`${user.avatarColor || 'bg-neutral-600'} text-neutral-200 text-sm font-medium`}>
                                                        {getInitials(user.name)}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <div className="flex flex-col flex-1 min-w-0">
                                                    <span className="text-sm font-medium truncate">{user.name}</span>
                                                    <span className="text-xs text-muted-foreground truncate">{user.email}</span>
                                                </div>
                                                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                            </DropdownMenuItem>
                                            <DropdownMenuSeparator />
                                        </>
                                    )}

                                    {/* Theme Toggle */}
                                    <div className="px-2 py-1.5 flex items-center justify-between">
                                        <span className="text-sm">Appearance</span>
                                        <ModeToggle variant="compact" />
                                    </div>

                                    <DropdownMenuSeparator />

                                    {/* Preferences - Removed since it's now in the header */}

                                    {/* Organization Settings - Smart expandable */}
                                    {organizations.length === 1 ? (
                                        <DropdownMenuItem onClick={() => navigate(`/${organizations[0].slug}`)}>
                                            <Settings className="mr-2 h-4 w-4" />
                                            Organization Settings
                                        </DropdownMenuItem>
                                    ) : (
                                        <>
                                            <DropdownMenuItem 
                                                onSelect={(e) => e.preventDefault()}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setIsOrgExpanded(!isOrgExpanded);
                                                }}
                                            >
                                                <Settings className="mr-2 h-4 w-4" />
                                                Organization Settings
                                                <ChevronRight className={`ml-auto h-4 w-4 transition-transform ${isOrgExpanded ? 'rotate-90' : ''}`} />
                                            </DropdownMenuItem>
                                            {isOrgExpanded && (
                                                <div className="mx-2 my-2 px-2 py-1.5 bg-muted/30 rounded-md border border-border/50">
                                                    {organizations.map((org) => (
                                                        <DropdownMenuItem
                                                            key={org.id}
                                                            className="flex items-center gap-2"
                                                            onClick={() => {
                                                                navigate(`/${org.slug}`);
                                                                setIsOrgExpanded(false);
                                                            }}
                                                        >
                                                            <Avatar className="h-5 w-5">
                                                                <AvatarFallback className="bg-zinc-200 text-zinc-800 dark:bg-zinc-700 dark:text-zinc-200 text-medium text-[0.55rem]">
                                                                    {org.name.slice(0, 2).toUpperCase()}
                                                                </AvatarFallback>
                                                            </Avatar>
                                                            <span className="flex-1 truncate text-sm">{org.name}</span>
                                                            {activeOrganization?.slug === org.slug && (
                                                                <Check className="h-4 w-4 text-muted-foreground" />
                                                            )}
                                                        </DropdownMenuItem>
                                                    ))}
                                                    <DropdownMenuSeparator className="my-1.5" />
                                                    <DropdownMenuItem
                                                        className="flex items-center gap-2"
                                                        onClick={() => {
                                                            navigate('/organizations/new');
                                                            setIsOrgExpanded(false);
                                                        }}
                                                    >
                                                        <Plus className="h-4 w-4" />
                                                        <span className="flex-1 truncate text-sm">New Organization</span>
                                                    </DropdownMenuItem>
                                                </div>
                                            )}
                                        </>
                                    )}

                                    {/* Help & Support */}
                                    <DropdownMenuItem onClick={() => navigate('/support')}>
                                        <HelpCircle className="mr-2 h-4 w-4" />
                                        Help & Support
                                    </DropdownMenuItem>

                                    <DropdownMenuSeparator />

                                    {/* Logout */}
                                    <DropdownMenuItem onClick={logout}>
                                        <LogOut className="mr-2 h-4 w-4" />
                                        Logout
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                            <Button variant="ghost" size="icon" className="md:hidden" onClick={toggleMobileMenu}>
                                {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                            </Button>
                        </div>
                    </div>

                    {/* Mobile menu */}
                    <div className={`md:hidden transition-all duration-300 ease-in-out ${isMobileMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`}>
                        <div className="py-4 space-y-4">
                            {/* User Info */}
                            {user && (
                                <div className="px-2 mb-4 pb-4 border-b">
                                    <div className="flex items-center gap-3">
                                        <Avatar className="h-10 w-10">
                                            <AvatarFallback className={`${user.avatarColor || 'bg-neutral-600'} text-neutral-200 text-sm font-medium`}>
                                                {getInitials(user.name)}
                                            </AvatarFallback>
                                        </Avatar>
                                        <div className="flex flex-col">
                                            <span className="text-sm font-medium">{user.name}</span>
                                            <span className="text-xs text-muted-foreground">{user.email}</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Navigation Links */}
                            <div className="space-y-1">
                                <Button
                                    variant={isLinkActive('/') ? "secondary" : "ghost"}
                                    className="w-full justify-start"
                                    onClick={() => { navigate('/today'); toggleMobileMenu(); }}
                                >
                                    Today
                                </Button>
                                <Button
                                    variant={isLinkActive('/plan/tasks') ? "secondary" : "ghost"}
                                    className="w-full justify-start"
                                    onClick={() => { navigate('/plan/tasks'); toggleMobileMenu(); }}
                                >
                                    All Tasks
                                </Button>
                                <Button
                                    variant={isLinkActive('/plan') && !isLinkActive('/plan/tasks') ? "secondary" : "ghost"}
                                    className="w-full justify-start"
                                    onClick={() => { navigate('/plan'); toggleMobileMenu(); }}
                                >
                                    Plan
                                </Button>
                                <Button
                                    variant={isLinkActive('/monitors') ? "secondary" : "ghost"}
                                    className="w-full justify-start"
                                    onClick={() => { navigate('/monitors'); toggleMobileMenu(); }}
                                >
                                    Monitor
                                </Button>
                                <Button
                                    variant={isLinkActive('/forms') ? "secondary" : "ghost"}
                                    className="w-full justify-start"
                                    onClick={() => { navigate('/forms'); toggleMobileMenu(); }}
                                >
                                    Form
                                </Button>
                                {/* <Button
                                    variant={isLinkActive('/reports') ? "secondary" : "ghost"}
                                    className="w-full justify-start"
                                    onClick={() => { navigate('/reports'); toggleMobileMenu(); }}
                                >
                                    Reports
                                </Button> */}
                            </div>

                            {/* Theme Toggle */}
                            <div className="px-2 pt-4 border-t">
                                <div className="flex items-center justify-between">
                                    <Label className="text-sm">Appearance</Label>
                                    <ModeToggle variant="compact" />
                                </div>
                            </div>

                            {/* Account Links */}
                            <div className="border-t pt-4 mt-4 space-y-1">
                                <Button
                                    variant="ghost"
                                    className="w-full justify-start"
                                    onClick={() => { navigate('/preferences'); toggleMobileMenu(); }}
                                >
                                    <User className="mr-2 h-4 w-4" />
                                    Preferences
                                </Button>
                                {organizations.length === 1 ? (
                                    <Button
                                        variant="ghost"
                                        className="w-full justify-start"
                                        onClick={() => { navigate(`/${organizations[0].slug}`); toggleMobileMenu(); }}
                                    >
                                        <Settings className="mr-2 h-4 w-4" />
                                        Organization Settings
                                    </Button>
                                ) : (
                                    <>
                                        <Button
                                            variant="ghost"
                                            className="w-full justify-start"
                                            onClick={() => setIsOrgExpanded(!isOrgExpanded)}
                                        >
                                            <Settings className="mr-2 h-4 w-4" />
                                            Organization Settings
                                            <ChevronRight className={`ml-auto h-4 w-4 transition-transform ${isOrgExpanded ? 'rotate-90' : ''}`} />
                                        </Button>
                                        {isOrgExpanded && (
                                            <div className="mx-2 my-2 px-2 py-1.5 bg-muted/30 rounded-md border border-border/50 space-y-1">
                                                {organizations.map((org) => (
                                                    <Button
                                                        key={org.id}
                                                        variant="ghost"
                                                        className="w-full justify-start h-auto py-2"
                                                        onClick={() => {
                                                            navigate(`/${org.slug}`);
                                                            setIsOrgExpanded(false);
                                                            toggleMobileMenu();
                                                        }}
                                                    >
                                                        <Avatar className="h-5 w-5 mr-2 shrink-0">
                                                            <AvatarFallback className="bg-zinc-200 text-zinc-800 dark:bg-zinc-700 dark:text-zinc-200 text-medium text-[0.55rem]">
                                                                {org.name.slice(0, 2).toUpperCase()}
                                                            </AvatarFallback>
                                                        </Avatar>
                                                        <span className="flex-1 truncate text-sm text-left">{org.name}</span>
                                                        {activeOrganization?.slug === org.slug && (
                                                            <Check className="h-4 w-4 text-muted-foreground ml-2" />
                                                        )}
                                                    </Button>
                                                ))}
                                                <Separator className="my-1.5" />
                                                <Button
                                                    variant="ghost"
                                                    className="w-full justify-start h-auto py-2"
                                                    onClick={() => {
                                                        navigate('/organizations/new');
                                                        setIsOrgExpanded(false);
                                                        toggleMobileMenu();
                                                    }}
                                                >
                                                    <Plus className="h-4 w-4 mr-2 shrink-0" />
                                                    <span className="flex-1 truncate text-sm text-left">New Organization</span>
                                                </Button>
                                            </div>
                                        )}
                                    </>
                                )}
                                <Button
                                    variant="ghost"
                                    className="w-full justify-start"
                                    onClick={() => { navigate('/support'); toggleMobileMenu(); }}
                                >
                                    <HelpCircle className="mr-2 h-4 w-4" />
                                    Help & Support
                                </Button>
                                <Button
                                    variant="ghost"
                                    className="w-full justify-start text-red-500 hover:text-red-500 hover:bg-red-500/10"
                                    onClick={() => { logout(); toggleMobileMenu(); }}
                                >
                                    <LogOut className="mr-2 h-4 w-4" />
                                    Logout
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </nav>
            <main className={`mt-16 ${noContainer ? '' : 'mx-auto px-4 sm:px-6 lg:px-8 py-8 h-[calc(100vh-4rem)]'}`}>
                {children}
            </main>
            <Button
                className="fixed group/statusBotIcon bottom-8 right-8 w-16 h-16 rounded-full bg-transparent hover:bg-transparent transition-all duration-500 hover:transition-all ease-in-out hover:animate-float-hover"   
                size="icon"
                onClick={() => setIsCommandPaletteOpen(true)}
            >
                <img 
                    src={statusBotSticker} 
                    alt="AI Assistant" 
                    className="w-16 h-16 absolute inset-0 transition-all duration-1000 group-hover/statusBotIcon:blur-md group-hover/statusBotIcon:scale-110 opacity-60" 
                />
                <img 
                    src={statusBotSticker} 
                    alt="AI Assistant" 
                    className="w-16 h-16 z-10 transition-all duration-500 group-hover/statusBotIcon:scale-110" 
                />
            </Button>
            <AICommandPalette 
                isOpen={isCommandPaletteOpen} 
                onClose={() => setIsCommandPaletteOpen(false)} 
                pageContext={currentAiPageContext}
                setPageContext={setCurrentAiPageContext}
                initialAskQuery={initialAskQuery}
                onAskConsumed={handleAskConsumed}
            />
            <Toaster />
        </div>
    );
};

export default Layout;
